import { DoctorAttendingGenerateAccountingApiPayload } from '@omecare/utils';
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import http from '@/utils/http.service';
import { simpleDoctorAttendingSchema } from '@/utils/zod/doctor-attending.schema';

const generateAccountingResponseSchema = z.object({
  id: z.string().uuid(),
  period: z.object({
    from: z.coerce.string(),
    to: z.coerce.string(),
  }),
  amountFeeCollected: z.coerce.number(),
  amountMedicalAssistantFee: z.coerce.number(),
  amountSubstituteMedicalAssistantFee: z.coerce.number(),
  attendingShare: z.coerce.number(),
  amountPaid: z.coerce.number(),
  amountPayBack: z.coerce.number(),
  amountCommissionFee: z.coerce.number(),
  amountFee: z.coerce.number(),
  amountAvailable: z.coerce.number(),
  medicalAssistantShare: z.object({
    doctorDuration: z.coerce.number(),
    siteDuration: z.coerce.number(),
    sharePercentage: z.coerce.number(),
  }),
  doctorAttending: simpleDoctorAttendingSchema,
});

export type GenerateOfficeDoctorAttendingAccountingResponse = z.infer<
  typeof generateAccountingResponseSchema
>;
const generateAccounting = (
  payload: DoctorAttendingGenerateAccountingApiPayload,
) => {
  return http.doctorAttendings
    .generateAccounting(payload)
    .then<GenerateOfficeDoctorAttendingAccountingResponse>(({ transform }) => {
      const result: any = transform.handle({
        attributes: {
          amountFeeCollected: true,
          amountMedicalAssistantFee: true,
          amountSubstituteMedicalAssistantFee: true,
          attendingShare: true,
          amountPaid: true,
          amountPayBack: true,
          amountCommissionFee: true,
          amountFee: true,
          amountAvailable: true,
          medicalAssistantShare: true,
          period: true,
        },
        relations: {
          doctorAttending: true,
        },
        schema: generateAccountingResponseSchema,
      });

      return {
        ...result,
        doctorAttending: result.doctorAttending!,
      };
    });
};

const useGenerateAccountinOfficeDoctorAttendingMutation = (): UseMutationResult<
  GenerateOfficeDoctorAttendingAccountingResponse,
  Error,
  DoctorAttendingGenerateAccountingApiPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generateAccounting,
    onSuccess() {
      toast.success(
        t('http.generate-accounting-office-doctor-attending.success'),
      );
      queryClient.invalidateQueries({
        queryKey: ['doctor-attendings-with-accountings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['attending-accountings'],
      });
    },
  });
};

export default useGenerateAccountinOfficeDoctorAttendingMutation;
